/**
 * The signed-in person's own desktops on one workstation (2026-10-06, PR 4 of the DocuSign fix).
 *
 * The /desktop page asks this before it reloads its embed onto a session the sign-in window names: when that
 * session is the person's only desktop there, the embed (which attaches to the person's own desktop when it is
 * not given one) already shows it, and reloading would only flash the page. Ids only; the list is the
 * workstation's own (a person's ticket lists only their desktops, the machine key all of them).
 */
import { fetchWithBrowserServiceAuth } from './baseUrl';

/** How long the page waits for the list before it reloads the embed anyway. */
export const OWN_DESKTOPS_WAIT_MS = 4000;

/** The ids of the person's running or starting desktops on that service, or null when the list could not be read. */
export async function ownDesktopIdsOn(base: string, uid: string): Promise<string[] | null> {
  const root = String(base || '').trim().replace(/\/+$/, '');
  if (!root || !uid) return null;
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const timeout = new Promise<null>((resolve) => { timer = setTimeout(() => resolve(null), OWN_DESKTOPS_WAIT_MS); });
    const res = await Promise.race([fetchWithBrowserServiceAuth(root, `${root}/api/desktop`), timeout]);
    if (!res || !res.ok) return null;
    const body = (await res.json()) as { sessions?: unknown } | null;
    if (!body || !Array.isArray(body.sessions)) return null;
    const ids: string[] = [];
    for (const s of body.sessions as Array<Record<string, unknown> | null>) {
      if (!s || s.userId !== uid || typeof s.id !== 'string') continue;
      if (s.status !== 'running' && s.status !== 'starting') continue;
      ids.push(s.id);
    }
    return ids;
  } catch {
    return null;
  } finally {
    if (timer) clearTimeout(timer);
  }
}
